/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {forkJoin, fromEvent, identity, Observable, of} from 'rxjs';
import {debounceTime, filter, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ESSService} from '../../+ess/employee-self.service';
import {REQUEST_OPTIONS, RESIZE_EVENT} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {NotificationFromSocket} from '../../inbox/shared/communications.interface';
import {CommunicationsService} from '../../inbox/shared/communications.service';
import {ApiService, Link} from '../../shared/api';
import {NotificationSocketService} from '../../shared/api/notification-socket.service';
import {AuthenticationService} from '../../shared/authentication';
import {KeycloakWrapperService} from '../../shared/authentication/keycloak-wrapper.service';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {Employee} from '../../shared/employees/employees.model';
import {ModalService} from '../../shared/modal';
import {WindowService} from '../../shared/ui/window.service';
import {User, UserService} from '../../shared/user';
import {isMobileDevice} from '../../shared/utils/browser';
import {isPresent} from '../../shared/utils/lang';
import {getInitials, isValidPhotoUrl} from '../../shared/utils/strings';
import {WorkflowV2Service} from '../../shared/workflow/workflow-v2.service';
import {AsideUtilsService} from '../aside/aside-utils.service';
import {CliCorePropertiesService} from '../cli-core-properties.service';

interface SearchResult {
  id: number;
  name: string;
  displayedId: string;
  employee: boolean;
  entity: boolean;
  peopleTreeNode: boolean;
  treeId?: number;
  links?: Link[];
}

/**
 * Top navigation navbar component, that displays a user menu, an access to messages and a search form
 */
@Component({
  selector: 'gp-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent extends Destroyable implements OnInit {

  public user: User;
  public navLogoURL: string;
  public searchTerm: UntypedFormControl = new UntypedFormControl();
  public searchResults: Array<SearchResult> = [];
  public isNotificationsVisible = false;
  public collapsedOpen: boolean;
  public isLoading = false;
  public employee: Employee;
  public knowledgeUri: string;
  public displayAsyncInfos = false;
  public asyncProcessesRunning: string[] = [];
  public isMobile = false;
  public isHovered = false;
  public profileDrawerOpen = false;

  @Input() showChatbot = false;
  @Output() chatbotToggle = new EventEmitter<boolean>();

  constructor(public rolesService: RolesService,
              public communicationsService: CommunicationsService,
              public workflowV2Service: WorkflowV2Service,
              private keycloakWrapperService: KeycloakWrapperService,
              private authenticationService: AuthenticationService,
              private router: Router,
              private userService: UserService,
              private api: ApiService,
              private cliCorePropertiesService: CliCorePropertiesService,
              private translate: TranslateService,
              private essService: ESSService,
              private modalService: ModalService,
              private windowService: WindowService,
              private asideUtilsService: AsideUtilsService,
              private notificationSocketService: NotificationSocketService) {
    super();
    this.cliCorePropertiesService.get().subscribe();
    this.displayAsyncInfos = localStorage.getItem('gp_display_async_infos') === 'true';
  }

  public ngOnInit() {
    this.isMobile = isMobileDevice();

    this.userService.getUser().pipe(
      map((user: User) => this.user = user),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    // Client brand logo shown on the left of the (now unified) top navbar.
    this.navLogoURL = this.windowService.isStaticImageAvailable('NAV_LOGO') ?
      `${this.api.file.prefix}/static/${this.windowService.getCliCoreNameFromHost()}/NAV_LOGO` : null;

    this.asideUtilsService.onNavLogoURLUpdated().pipe(
      tap((navLogoURL: string) => this.navLogoURL = navLogoURL),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.translate.onLangChange.pipe(
      switchMap(() => this.userService.getKnowledgeUri()),
      tap((knowledgeUri: string) => this.knowledgeUri = knowledgeUri),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.searchTerm.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(650),
        tap(() => this.isLoading = true),
        switchMap((term: string) => this.search(term))
      )
      .subscribe(result => {
        this.searchResults = Array.isArray(result) ? result : [];
        this.isLoading = false;
      });

    this.essService.currentUser
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.employee = this.essService.employee;
      });

    fromEvent(window, 'resize')
      .pipe(
        takeUntil(this.componentDestroyed$),
        startWith(window.dispatchEvent(RESIZE_EVENT)),
        debounceTime(400)
      )
      .subscribe((event) => {
        const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        if (windowWidth > 768) {
          this.collapsedOpen = false;
          this.modalService.modalClosed.emit('aside');
        }
      });

    if (this.displayAsyncInfos) {
      this.notificationSocketService.messages$()
        .pipe(
          debounceTime(1000),
          filter((msg: NotificationFromSocket<any>) => msg.notificationType === 'ASYNC_PROCESS'),
          switchMap(() => this.refreshAsyncProcessRunning()),
          tap((allAsyncProcessesRunning: string[]) => {
            this.asyncProcessesRunning = allAsyncProcessesRunning.flatMap(identity);
          }),
          takeUntil(this.componentDestroyed$)
        )
        .subscribe();
    }
  }

  private refreshAsyncProcessRunning(): Observable<string[]> {
    return forkJoin(
      this.api.silent.ga.get('/status/async'),
      this.api.silent.elasticquery.get('/status/async'),
      this.api.silent.payroll.get('/status/async'));
  }

  public hasAsyncProcessesRunning(): boolean {
    return this.asyncProcessesRunning && this.asyncProcessesRunning.length > 0;
  }

  public open(result: SearchResult): void {
    if (result.peopleTreeNode) {
      this.router.navigate(['employees/tree/manage', {
        treeId: result.treeId,
        leafId: result.id
      }]);
    } else if (result.employee) {
      this.router.navigate(['employees/files', result.id]);
    } else if (result.entity) {
      this.router.navigate(['company/manage', {
        treeId: result.treeId || '',
        orgId: result.id
      }]);
    }
    this.searchResults = [];
    this.searchTerm.setValue('');
  }

  public logout(): void {
    // hack in case of user using IDP, we force clear all sessions
    this.api.authent.post('/keycloak/logout', null).pipe(
      tap(() => this.authenticationService.logout())
    ).subscribe();
  }

  public changePassword(): void {
    this.keycloakWrapperService.login({
      action: 'UPDATE_PASSWORD'
    });
  }

  public showNotificationCard(visible: boolean): void {
    this.isNotificationsVisible = visible;
  }

  public clear(): void {
    this.searchTerm.setValue('');
    this.searchResults = [];
  }

  public search(term: string): Observable<any> {
    if (!isPresent(term) || `${term}` === '') {
      return of([]);
    }

    return this.api.get(`/ga/api/v1/search?input=${term}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map((response) => {
          if (response.status === 204) {
            return [];
          }
          return response.body;
        })
      );
  }

  public openKnowledge(): void {
    if (this.knowledgeUri && this.knowledgeUri !== '') {
      window.open(this.knowledgeUri);
    }
  }

  public toggleChatbot(): void {
    this.showChatbot = !this.showChatbot;
    this.chatbotToggle.emit(this.showChatbot);
  }

  public hasNavPhoto(): boolean {
    return isValidPhotoUrl(this.employee?.photoLink?.href);
  }

  public getSearchResultInitials(result: any): string {
    return getInitials(result.name);
  }

  public getNavInitials(): string {
    const first = this.employee?.firstName || this.user?.firstName || '';
    const last = this.employee?.lastName || this.user?.lastName || '';
    return getInitials(first, last);
  }
}
